import React, { useState, useEffect } from 'react'
import { AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Input,
} from '../ui'
import {
  useCreateOrganization,
  useUpdateOrganization,
} from '../../hooks/organizations'
import type {
  Organization,
  CreateOrganizationDto,
  UpdateOrganizationDto,
} from '../../types/organization.types'
import { LoadingSpinner } from '../ui/loading-states'

interface OrganizationFormData {
  name: string
  legal_name: string
  bin: string
  code_1c: string
  legal_address: string
  actual_address: string
  director_name: string
  phone: string
  email: string
  bank_name: string
  bank_account: string
  bik: string
  is_headquarters: boolean
  forecast_department_id: string
  forecast_department_name: string
}

interface OrganizationFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organization?: Organization | null
  mode: 'create' | 'edit'
}

const initialFormData: OrganizationFormData = {
  name: '',
  legal_name: '',
  bin: '',
  code_1c: '',
  legal_address: '',
  actual_address: '',
  director_name: '',
  phone: '',
  email: '',
  bank_name: '',
  bank_account: '',
  bik: '',
  is_headquarters: false,
  forecast_department_id: '',
  forecast_department_name: '',
}

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function OrganizationForm({ open, onOpenChange, organization, mode }: OrganizationFormProps) {
  const [formData, setFormData] = useState<OrganizationFormData>(initialFormData)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const createOrganization = useCreateOrganization()
  const updateOrganization = useUpdateOrganization()

  const isLoading = createOrganization.isPending || updateOrganization.isPending

  // Initialize form data when dialog opens or organization changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && organization) {
        setFormData({
          name: organization.name || '',
          legal_name: organization.legal_name || '',
          bin: organization.bin || '',
          code_1c: organization.code_1c || '',
          legal_address: organization.legal_address || '',
          actual_address: organization.actual_address || '',
          director_name: organization.director_name || '',
          phone: organization.phone || '',
          email: organization.email || '',
          bank_name: organization.bank_name || '',
          bank_account: organization.bank_account || '',
          bik: organization.bik || '',
          is_headquarters: organization.is_headquarters || false,
          forecast_department_id: organization.forecast_department_id || '',
          forecast_department_name: organization.forecast_department_name || '',
        })
      } else {
        setFormData(initialFormData)
      }
      setErrors({})
    }
  }, [open, mode, organization])

  const handleInputChange = (field: keyof OrganizationFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setFormData(prev => ({ ...prev, [field]: value }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Название организации обязательно'
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Название должно содержать минимум 2 символа'
    }

    if (!formData.bin.trim()) {
      newErrors.bin = 'БИН обязателен'
    } else if (!/^\d{12}$/.test(formData.bin.trim())) {
      newErrors.bin = 'БИН должен содержать ровно 12 цифр'
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Некорректный формат email'
    }

    if (
      formData.forecast_department_id &&
      !UUID_REGEX.test(formData.forecast_department_id.trim())
    ) {
      newErrors.forecast_department_id =
        'UUID должен быть в формате xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      const dataToSend: CreateOrganizationDto | UpdateOrganizationDto = {
        name: formData.name.trim(),
        bin: formData.bin.trim(),
        is_headquarters: formData.is_headquarters,
      }

      // Add optional fields only if they have values
      if (formData.legal_name.trim()) dataToSend.legal_name = formData.legal_name.trim()
      if (formData.code_1c.trim()) dataToSend.code_1c = formData.code_1c.trim()
      if (formData.legal_address.trim()) dataToSend.legal_address = formData.legal_address.trim()
      if (formData.actual_address.trim()) dataToSend.actual_address = formData.actual_address.trim()
      if (formData.director_name.trim()) dataToSend.director_name = formData.director_name.trim()
      if (formData.phone.trim()) dataToSend.phone = formData.phone.trim()
      if (formData.email.trim()) dataToSend.email = formData.email.trim()
      if (formData.bank_name.trim()) dataToSend.bank_name = formData.bank_name.trim()
      if (formData.bank_account.trim()) dataToSend.bank_account = formData.bank_account.trim()
      if (formData.bik.trim()) dataToSend.bik = formData.bik.trim()
      // Forecast fields: при edit отправляем null явно если поле пустое,
      // чтобы backend сбросил значение в БД (иначе Prisma игнорирует
      // отсутствующий ключ и значение остаётся прежним).
      if (formData.forecast_department_id.trim()) {
        dataToSend.forecast_department_id =
          formData.forecast_department_id.trim()
      } else if (mode === 'edit') {
        ;(dataToSend as Record<string, unknown>).forecast_department_id = null
      }
      if (formData.forecast_department_name.trim()) {
        dataToSend.forecast_department_name =
          formData.forecast_department_name.trim()
      } else if (mode === 'edit') {
        ;(dataToSend as Record<string, unknown>).forecast_department_name = null
      }

      if (mode === 'create') {
        await createOrganization.mutateAsync(dataToSend as CreateOrganizationDto)
      } else if (mode === 'edit' && organization) {
        await updateOrganization.mutateAsync({
          id: organization.id,
          data: dataToSend as UpdateOrganizationDto
        })
      }
      onOpenChange(false)
    } catch (error) {
      console.error('Form submission error:', error)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Создать организацию' : 'Редактировать организацию'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Заполните информацию для создания нового юридического лица.'
              : 'Измените информацию об организации.'
            }
          </DialogDescription>
          {!isLoading && <DialogClose onClose={handleClose} />}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900 border-b pb-2">
              Основная информация
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Organization Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Краткое название *
                </label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  placeholder="Ресторан Астана"
                  disabled={isLoading}
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <div className="flex items-center mt-1 text-sm text-red-600">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.name}
                  </div>
                )}
              </div>

              {/* Legal Name */}
              <div>
                <label htmlFor="legal_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Полное наименование
                </label>
                <Input
                  id="legal_name"
                  type="text"
                  value={formData.legal_name}
                  onChange={handleInputChange('legal_name')}
                  placeholder='ТОО "Ресторан Астана"'
                  disabled={isLoading}
                />
              </div>

              {/* BIN */}
              <div>
                <label htmlFor="bin" className="block text-sm font-medium text-gray-700 mb-1">
                  БИН *
                </label>
                <Input
                  id="bin"
                  type="text"
                  value={formData.bin}
                  onChange={handleInputChange('bin')}
                  placeholder="123456789012"
                  maxLength={12}
                  disabled={isLoading}
                  className={errors.bin ? 'border-red-500' : ''}
                />
                {errors.bin && (
                  <div className="flex items-center mt-1 text-sm text-red-600">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.bin}
                  </div>
                )}
              </div>

              {/* Code 1C */}
              <div>
                <label htmlFor="code_1c" className="block text-sm font-medium text-gray-700 mb-1">
                  Код 1С
                </label>
                <Input
                  id="code_1c"
                  type="text"
                  value={formData.code_1c}
                  onChange={handleInputChange('code_1c')}
                  placeholder="ORG-001"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Headquarters checkbox */}
            <div className="flex items-center">
              <input
                id="is_headquarters"
                type="checkbox"
                checked={formData.is_headquarters}
                onChange={handleInputChange('is_headquarters')}
                disabled={isLoading}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_headquarters" className="ml-2 block text-sm text-gray-700">
                Головная организация холдинга
              </label>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900 border-b pb-2">
              Контактная информация
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Director */}
              <div>
                <label htmlFor="director_name" className="block text-sm font-medium text-gray-700 mb-1">
                  ФИО директора
                </label>
                <Input
                  id="director_name"
                  type="text"
                  value={formData.director_name}
                  onChange={handleInputChange('director_name')}
                  placeholder="Иванов Иван Иванович"
                  disabled={isLoading}
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Телефон
                </label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange('phone')}
                  placeholder="+7 (777) 123-45-67"
                  disabled={isLoading}
                />
              </div>

              {/* Email */}
              <div className="md:col-span-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  placeholder="office@company.kz"
                  disabled={isLoading}
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <div className="flex items-center mt-1 text-sm text-red-600">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.email}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Addresses */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900 border-b pb-2">
              Адреса
            </h3>

            <div className="grid grid-cols-1 gap-4">
              {/* Legal Address */}
              <div>
                <label htmlFor="legal_address" className="block text-sm font-medium text-gray-700 mb-1">
                  Юридический адрес
                </label>
                <Input
                  id="legal_address"
                  type="text"
                  value={formData.legal_address}
                  onChange={handleInputChange('legal_address')}
                  placeholder="г. Астана, ул. Примерная, д. 1"
                  disabled={isLoading}
                />
              </div>

              {/* Actual Address */}
              <div>
                <label htmlFor="actual_address" className="block text-sm font-medium text-gray-700 mb-1">
                  Фактический адрес
                </label>
                <Input
                  id="actual_address"
                  type="text"
                  value={formData.actual_address}
                  onChange={handleInputChange('actual_address')}
                  placeholder="г. Астана, ул. Примерная, д. 1"
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900 border-b pb-2">
              Банковские реквизиты
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Bank Name */}
              <div>
                <label htmlFor="bank_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Название банка
                </label>
                <Input
                  id="bank_name"
                  type="text"
                  value={formData.bank_name}
                  onChange={handleInputChange('bank_name')}
                  placeholder="АО Kaspi Bank"
                  disabled={isLoading}
                />
              </div>

              {/* Bank Account */}
              <div>
                <label htmlFor="bank_account" className="block text-sm font-medium text-gray-700 mb-1">
                  ИИК (Расчётный счёт)
                </label>
                <Input
                  id="bank_account"
                  type="text"
                  value={formData.bank_account}
                  onChange={handleInputChange('bank_account')}
                  placeholder="KZ123456789012345678"
                  disabled={isLoading}
                />
              </div>

              {/* BIK */}
              <div>
                <label htmlFor="bik" className="block text-sm font-medium text-gray-700 mb-1">
                  БИК банка
                </label>
                <Input
                  id="bik"
                  type="text"
                  value={formData.bik}
                  onChange={handleInputChange('bik')}
                  placeholder="CASPKZKA"
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {/* Smart Schedule — Forecast API mapping */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900 border-b pb-2">
              Smart Schedule (прогноз продаж)
            </h3>
            <p className="text-xs text-gray-500 -mt-2">
              UUID отдела из сервиса прогноза продаж (aqniet.site). Используется для
              автогенерации графиков. Получить можно через{' '}
              <code className="px-1 bg-gray-100 rounded">
                GET https://aqniet.site/api/forecast/batch
              </code>{' '}
              — ищите свою точку в поле <code>department_id</code>.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="forecast_department_id"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  UUID отдела в forecast API
                </label>
                <Input
                  id="forecast_department_id"
                  type="text"
                  value={formData.forecast_department_id}
                  onChange={handleInputChange('forecast_department_id')}
                  placeholder="0d30c200-87b5-45a5-89f0-eb76e2892b4a"
                  disabled={isLoading}
                  className={errors.forecast_department_id ? 'border-red-500' : ''}
                />
                {errors.forecast_department_id && (
                  <div className="flex items-center mt-1 text-sm text-red-600">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.forecast_department_id}
                  </div>
                )}
              </div>

              <div>
                <label
                  htmlFor="forecast_department_name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Название отдела (для отображения)
                </label>
                <Input
                  id="forecast_department_name"
                  type="text"
                  value={formData.forecast_department_name}
                  onChange={handleInputChange('forecast_department_name')}
                  placeholder="Tary Almaty"
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {/* Show API errors */}
          {(createOrganization.error || updateOrganization.error) && (
            <div className="flex items-center p-3 text-sm text-red-700 bg-red-100 rounded-md">
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>
                {(createOrganization.error as any)?.message ||
                 (updateOrganization.error as any)?.message ||
                 'Произошла ошибка при сохранении'}
              </span>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Отменить
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="min-w-[100px]"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner className="mr-2 h-4 w-4" />
                  {mode === 'create' ? 'Создание...' : 'Сохранение...'}
                </>
              ) : (
                mode === 'create' ? 'Создать' : 'Сохранить'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
